import type { TrophyId } from '@crowdfpl/domain/gamification';
import { Button } from '@crowdfpl/ui-web/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@crowdfpl/ui-web/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@crowdfpl/ui-web/sheet';
import { Skeleton } from '@crowdfpl/ui-web/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@crowdfpl/ui-web/tabs';
import { AlertCircle, TrendingUp, Trophy } from 'lucide-react';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { GamificationHeroEvent } from '@/components/gamification/gamification-hero-event';
import { ManagerTiersView } from '@/components/gamification/manager-tiers-view';
import { TrophySystemView } from '@/components/gamification/trophy-system-view';
import { trackUmamiEvent } from '@/helpers/analytics';
import { formatGamificationEvidenceSummary } from '@/helpers/gamification-evidence';
import { cn } from '@/helpers/utils';
import {
  useMarkGamificationEventsSeen,
  useMyGamification,
  usePublicGamification,
  useSetGamificationPin,
} from '@/hooks/data/gamification';
import { useIsMobile } from '@/hooks/utils/use-mobile';
import { notify } from '@/services/notifications';
import {
  type ProgressTrophiesTab,
  useProgressTrophiesModalStore,
} from '@/stores/progress-trophies-modal';
import type { GamificationMeState, GamificationPublicState } from '@/types/gamification';

type ModalPayload = GamificationMeState | GamificationPublicState;

function LoadingState(): ReactElement {
  return (
    <div className="space-y-3" data-testid="progress-trophies-loading">
      <Skeleton className="h-20 w-full rounded-lg" />
      <Skeleton className="h-20 w-full rounded-lg" />
      <Skeleton className="h-20 w-full rounded-lg" />
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }): ReactElement {
  return (
    <div className="flex flex-col items-start gap-3 rounded-lg border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center gap-2 text-foreground">
        <AlertCircle className="h-4 w-4 text-destructive" aria-hidden />
        <p className="text-sm font-medium">Could not load progress data.</p>
      </div>
      <p className="text-xs text-muted-foreground">Please try again.</p>
      <Button type="button" variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

function ShellBody({
  activeTab,
  onTabChange,
  isLoading,
  isError,
  onRetry,
  data,
  heroSlot,
  isOwnProfile,
  ownerHandle,
  pinnedTrophyId,
  onPin,
  isPinPending,
}: {
  activeTab: ProgressTrophiesTab;
  onTabChange: (tab: ProgressTrophiesTab) => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  data: ModalPayload | undefined;
  heroSlot: ReactElement | null;
  isOwnProfile: boolean;
  ownerHandle: string | null;
  pinnedTrophyId: TrophyId | null;
  onPin: (trophyId: TrophyId) => void;
  isPinPending: boolean;
}): ReactElement {
  const showContent = !isLoading && !isError && data;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="shrink-0 border-b border-border bg-background px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))] sm:px-6">
        <h2 className="flex min-w-0 items-baseline gap-2 text-lg font-semibold tracking-tight text-foreground">
          <span className="shrink-0">Progress & Trophies</span>
          {ownerHandle ? (
            <span
              className="truncate text-base font-medium text-muted-foreground"
              data-testid="progress-trophies-owner"
            >
              @{ownerHandle}
            </span>
          ) : null}
        </h2>
      </header>
      <Tabs
        value={activeTab}
        onValueChange={value => onTabChange(value as ProgressTrophiesTab)}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <div className="shrink-0 border-b border-border px-4 py-3 sm:px-6">
          <TabsList className="grid h-auto w-full grid-cols-2 p-1">
            <TabsTrigger value="progress" className="h-8 gap-1.5 py-0 text-[13px] font-semibold">
              <TrendingUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Progress
            </TabsTrigger>
            <TabsTrigger value="trophies" className="h-8 gap-1.5 py-0 text-[13px] font-semibold">
              <Trophy className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Trophies
            </TabsTrigger>
          </TabsList>
        </div>
        {showContent ? (
          <>
            <TabsContent value="progress" className="mt-0 min-h-0 flex-1 overflow-hidden">
              <div className="h-full space-y-4 overflow-y-auto px-4 pb-5 pt-4 sm:px-6">
                {heroSlot}
                <ManagerTiersView progress={data.progress} />
              </div>
            </TabsContent>
            <TabsContent value="trophies" className="mt-0 min-h-0 flex-1 overflow-hidden">
              <div className="h-full space-y-4 overflow-y-auto px-4 pb-5 pt-4 sm:px-6">
                {heroSlot}
                <TrophySystemView
                  trophies={data.trophies}
                  isOwnProfile={isOwnProfile}
                  pinnedTrophyId={pinnedTrophyId}
                  onPin={onPin}
                  isPinPending={isPinPending}
                />
              </div>
            </TabsContent>
          </>
        ) : (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-5 pt-4 sm:px-6">
            {isLoading ? <LoadingState /> : null}
            {!isLoading && (isError || !data) ? <ErrorState onRetry={onRetry} /> : null}
          </div>
        )}
      </Tabs>
    </div>
  );
}

export function ProgressTrophiesModal(): ReactElement {
  const isMobile = useIsMobile();
  const isOpen = useProgressTrophiesModalStore(state => state.isOpen);
  const defaultTab = useProgressTrophiesModalStore(state => state.defaultTab);
  const readMode = useProgressTrophiesModalStore(state => state.readMode);
  const targetUserId = useProgressTrophiesModalStore(state => state.targetUserId);
  const targetUsername = useProgressTrophiesModalStore(state => state.targetUsername);
  const close = useProgressTrophiesModalStore(state => state.close);
  const [activeTab, setActiveTab] = useState<ProgressTrophiesTab>(defaultTab);
  const ownQuery = useMyGamification();
  const publicQuery = usePublicGamification(readMode === 'public' ? targetUserId : null);
  const { mutate: markEventsSeen } = useMarkGamificationEventsSeen();
  const setPin = useSetGamificationPin();
  const previousOpenRef = useRef<boolean>(isOpen);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  // Tracks which latest_event_id was marked for the current open session so we
  // still mark-seen when private data arrives after the modal already opened.
  const markedSeenEventIdRef = useRef<string | null>(null);
  const ownData = ownQuery.data;
  const modeQuery = readMode === 'own' ? ownQuery : publicQuery;
  const isOwnProfile = readMode === 'own';
  const ownerHandle = !isOwnProfile && targetUsername ? targetUsername : null;
  const accessibleTitle = ownerHandle
    ? `Progress and trophies for @${ownerHandle}`
    : 'Progress and trophies modal';
  const accessibleDescription = ownerHandle
    ? `View @${ownerHandle}'s current progress and unlocked trophies.`
    : 'Track your current progress and unlocked trophies.';
  const pinnedTrophyId = modeQuery.data?.pinned.trophy_id ?? null;

  const handlePin = (trophyId: TrophyId): void => {
    setPin.mutate(
      { trophyId },
      {
        onError: () => notify.error('Could not pin trophy. Try again.'),
      },
    );
  };

  const handleTabChange = (tab: ProgressTrophiesTab): void => {
    if (tab !== activeTab) {
      trackUmamiEvent('gamification_tab_change', { tab, read_mode: readMode });
    }
    setActiveTab(tab);
  };

  const heroEvent = readMode === 'own' ? (ownData?.updates.hero_event ?? null) : null;
  const matchingTrophy = heroEvent?.trophy_id
    ? ownData?.trophies.find(trophy => trophy.trophy_id === heroEvent.trophy_id)
    : undefined;
  const heroSlot =
    heroEvent != null ? (
      <GamificationHeroEvent
        event={heroEvent}
        gameweekNumber={
          matchingTrophy?.first_unlocked_gameweek ?? ownData?.processing.latest_finalized_gameweek ?? null
        }
        evidenceSummary={formatGamificationEvidenceSummary(matchingTrophy?.evidence)}
      />
    ) : null;

  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab, isOpen]);

  useEffect(() => {
    const wasOpen = previousOpenRef.current;
    const justOpened = !wasOpen && isOpen;

    if (justOpened) {
      markedSeenEventIdRef.current = null;
      if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
        triggerElementRef.current = document.activeElement;
      } else {
        triggerElementRef.current = null;
      }
    }

    if (isOpen && readMode === 'own') {
      // Capture before optimistic update so rollback restores the same red-dot source id.
      const capturedLatestEventId = ownData?.updates.latest_event_id ?? null;
      const hasUnseen = ownData?.updates.has_unseen === true;
      if (
        hasUnseen &&
        capturedLatestEventId &&
        markedSeenEventIdRef.current !== capturedLatestEventId
      ) {
        markedSeenEventIdRef.current = capturedLatestEventId;
        markEventsSeen({ latestEventId: capturedLatestEventId });
      }
    }

    if (wasOpen && !isOpen) {
      markedSeenEventIdRef.current = null;
      if (triggerElementRef.current && typeof window !== 'undefined') {
        const trigger = triggerElementRef.current;
        window.requestAnimationFrame(() => {
          trigger.focus();
        });
      }
    }

    previousOpenRef.current = isOpen;
  }, [isOpen, markEventsSeen, ownData?.updates.has_unseen, ownData?.updates.latest_event_id, readMode]);

  const handleRetry = (): void => {
    void modeQuery.refetch();
  };

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      close();
    }
  };

  const body = (
    <ShellBody
      activeTab={activeTab}
      onTabChange={handleTabChange}
      isLoading={modeQuery.isLoading && !modeQuery.data}
      isError={modeQuery.isError}
      onRetry={handleRetry}
      data={modeQuery.data}
      heroSlot={heroSlot}
      isOwnProfile={isOwnProfile}
      ownerHandle={ownerHandle}
      pinnedTrophyId={pinnedTrophyId}
      onPin={handlePin}
      isPinPending={setPin.isPending}
    />
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className={cn(
            'flex h-dvh max-h-dvh flex-col gap-0 overflow-hidden p-0 pb-[env(safe-area-inset-bottom)] sm:max-w-full',
            '[&>button[type="button"]]:right-3 [&>button[type="button"]]:top-[max(0.75rem,env(safe-area-inset-top))]',
            'motion-reduce:duration-0 motion-reduce:transition-none',
          )}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{accessibleTitle}</SheetTitle>
            <SheetDescription>{accessibleDescription}</SheetDescription>
          </SheetHeader>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'flex h-[92vh] max-h-[92vh] max-w-3xl flex-col gap-0 overflow-hidden p-0',
          '[&>button[type="button"]]:right-4 [&>button[type="button"]]:top-4',
          'motion-reduce:duration-0 motion-reduce:transition-none',
        )}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{accessibleTitle}</DialogTitle>
          <DialogDescription>{accessibleDescription}</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
